/**
 * Chiamate della pagina «Profilo e sicurezza» (ADR 0030 §3): sessioni attive,
 * cambio della password, registro degli accessi, nuovi codici di recupero e
 * telefoni registrati per le notifiche.
 *
 * Le rotte di `auth` sono del gateway, quella dei telefoni del servizio
 * `notifications`. Sono nuove: finché il backend non le pubblica rispondono 404
 * (o 503 se il servizio è giù), e la pagina mostra la sezione come non
 * disponibile invece di rompersi (`isUnavailable`).
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano invece
 * che generati dagli schemi OpenAPI. `account.test.ts` presidia metodo,
 * percorso e corpo di ogni chiamata.
 */

import { ApiError, type ApiClient } from './client';

/** Prefisso delle rotte di autenticazione del gateway. */
const AUTH = '/api/v1/auth';

/**
 * Opzioni delle rotte che chiedono una credenziale (password attuale, codice):
 * un 401 lì è la risposta a una credenziale sbagliata, non una sessione scaduta.
 */
const CREDENTIAL_ROUTE = { notifyUnauthenticated: false } as const;

/** Chi può entrare con nome utente e password (ADR 0033). */
export type PasswordLoginMode = 'all' | 'admins' | 'off';

const PASSWORD_MODES: readonly PasswordLoginMode[] = ['all', 'admins', 'off'];

/** Modi di accesso accesi sul gateway. */
export interface LoginMethods {
  /** True se si entra (e ci si registra) con Google. */
  google: boolean;
  /** Chi può usare nome utente e password. */
  password: PasswordLoginMode;
}

/** Valori da usare finché il gateway non risponde, o se non risponde: come prima di ADR 0033. */
export const PASSWORD_ONLY: LoginMethods = { google: false, password: 'all' };

/** Una sessione aperta dall'utente, su questo o su un altro dispositivo. */
export interface AuthSession {
  /** Identificativo della sessione. */
  id: string;
  /** True per la sessione da cui arriva la richiesta. */
  current: boolean;
  /** Apertura, ISO 8601. */
  created_at: string;
  /** Ultimo uso, ISO 8601; `null` se non registrato. */
  last_used_at: string | null;
  /** Indirizzo IP dell'ultimo uso; vuoto se non noto. */
  ip: string;
  /** Browser e sistema, come li descrive il gateway; vuoto se non noto. */
  device: string;
}

/** Un evento del registro degli accessi. */
export interface AuthEvent {
  /** Istante, ISO 8601. */
  at: string;
  /** Tipo di evento (`login`, `login_failed`, `logout`, `password_changed`, …). */
  type: string;
  /** Indirizzo IP; vuoto se non noto. */
  ip: string;
  /** Browser e sistema; vuoto se non noto. */
  device: string;
}

/** Un telefono registrato per le notifiche push. */
export interface PushDevice {
  /** Identificativo della registrazione. */
  id: string;
  /** Piattaforma (`ios`, `android`). */
  platform: string;
  /** Nome del dispositivo scelto dall'app; vuoto se non c'è. */
  label: string;
  /** Ultimo contatto, ISO 8601; `null` se mai visto. */
  last_seen_at: string | null;
}

/** Dati del cambio della password. */
export interface PasswordChange {
  /** Password attuale. */
  current_password: string;
  /** Password nuova. */
  new_password: string;
}

/** Eventi che l'utente può scegliere di ricevere (ADR 0030 §7). */
export const NOTIFICATION_EVENTS = [
  'relay_response',
  'set_error',
  'site_published',
  'quota_warning',
  'task_proposed',
  'runtime_paused',
  'set_invitation',
  'site_alert',
  'billing',
] as const;

/** Un evento di notifica. */
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

/** La scelta dell'utente per un evento. */
export interface NotificationPreference {
  /** Evento. */
  event: NotificationEvent;
  /** Notifica push sui telefoni. */
  push: boolean;
  /** Email (canale non ancora disponibile). */
  email: boolean;
  /** False se la piattaforma non invia ancora l'evento (oggi li invia tutti, ADR 0032 §10). */
  available: boolean;
}

/** Preferenze delle notifiche dell'utente. */
export interface NotificationPreferences {
  /** Un elemento per evento, nell'ordine del catalogo. */
  events: NotificationPreference[];
  /** False finché il canale email non esiste. */
  email_available: boolean;
}

/** Stato di un'esportazione dei dati (ADR 0031). */
export type ExportStatus = 'none' | 'pending' | 'running' | 'ready' | 'failed' | 'expired';

/** Ultima richiesta di esportazione dei dati. */
export interface DataExport {
  /** Stato. */
  status: ExportStatus;
  /** Richiesta, ISO 8601. */
  requested_at: string | null;
  /** Fine della raccolta, ISO 8601. */
  completed_at: string | null;
  /** Fine della validità del collegamento, ISO 8601. */
  expires_at: string | null;
  /** Dimensione dell'archivio pronto, in byte. */
  size_bytes: number;
  /** Fonti che non hanno risposto. */
  missing: string[];
}

/** Percorso di scaricamento dell'archivio dei dati (stessa origine, cookie di sessione). */
export const EXPORT_DOWNLOAD_PATH = '/api/v1/me/export/download';

/** Codice del 409 con un'esportazione già in corso. */
export const EXPORT_IN_PROGRESS = 'export_in_progress';

/** Codice del 409 sulla cancellazione chiesta da un amministratore. */
export const ADMIN_CANNOT_DELETE = 'admin_cannot_delete';

/** Codice del 400 quando il nome utente scritto non coincide. */
export const CONFIRMATION_MISMATCH = 'confirmation_mismatch';

/** Codice del 400 quando la password non è quella attuale. */
export const INVALID_CURRENT_PASSWORD = 'invalid_current_password';

/** Codice del 409 quando l'accesso con Google è troppo vecchio per confermare la cancellazione. */
export const REAUTH_REQUIRED = 'reauth_required';

/** Codice del 400 quando la cancellazione senza password non vale per questa sessione. */
export const PASSWORD_REQUIRED = 'password_required';

/** Codice del 503 quando la cancellazione non si è conclusa. */
export const DELETION_INCOMPLETE = 'deletion_incomplete';

/**
 * Dice se un errore significa «questa funzione qui non c'è (ancora)».
 *
 * 404 e 405: la rotta non esiste nel backend in esercizio. 501 e 503: il
 * servizio la dichiara non disponibile o è giù. In tutti i casi la pagina
 * mostra la sezione come non disponibile, senza un errore da riprovare.
 *
 * @param caught Errore intercettato.
 * @returns True per un errore di disponibilità.
 */
export function isUnavailable(caught: unknown): boolean {
  return caught instanceof ApiError && [404, 405, 501, 503].includes(caught.status);
}

/**
 * Estrae i codici di recupero da una risposta, qualunque sia il nome del campo.
 *
 * Il gateway usa `recovery_codes` nella registrazione (ADR 0003); la rotta nuova
 * potrebbe chiamarlo `codes`. Si accettano entrambi, e un elenco nudo.
 *
 * @param body Corpo della risposta.
 * @returns I codici, vuoto se il corpo non ne contiene.
 */
export function recoveryCodesOf(body: unknown): string[] {
  const list = Array.isArray(body)
    ? body
    : typeof body === 'object' && body !== null
      ? ((body as Record<string, unknown>).recovery_codes ?? (body as Record<string, unknown>).codes)
      : null;
  return Array.isArray(list) ? list.filter((code): code is string => typeof code === 'string') : [];
}

/** Operazioni della pagina del profilo. */
export class AccountApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Elenca le sessioni aperte dall'utente.
   *
   * @returns Le sessioni, la corrente compresa.
   */
  listSessions(): Promise<AuthSession[]> {
    return this.client.request<AuthSession[]>('GET', `${AUTH}/sessions`);
  }

  /**
   * Chiude una sessione.
   *
   * @param sessionId Sessione da chiudere.
   * @returns Nulla.
   */
  revokeSession(sessionId: string): Promise<void> {
    return this.client.request<void>('DELETE', `${AUTH}/sessions/${encodeURIComponent(sessionId)}`);
  }

  /**
   * Chiude tutte le sessioni tranne quella da cui arriva la richiesta.
   *
   * @returns Nulla.
   */
  async revokeOtherSessions(): Promise<void> {
    await this.client.request<unknown>('POST', `${AUTH}/sessions/revoke-others`);
  }

  /**
   * Cambia la password.
   *
   * @param change Password attuale e nuova.
   * @returns Nulla.
   * @throws ApiError Con `code` `invalid_current_password` o `password_policy`.
   */
  async changePassword(change: PasswordChange): Promise<void> {
    await this.client.request<unknown>('POST', `${AUTH}/password`, change, CREDENTIAL_ROUTE);
  }

  /**
   * Legge gli ultimi eventi del registro degli accessi.
   *
   * @param limit Numero massimo di eventi.
   * @returns Gli eventi, dal più recente.
   */
  listEvents(limit = 50): Promise<AuthEvent[]> {
    return this.client.request<AuthEvent[]>('GET', `${AUTH}/me/events?limit=${String(limit)}`);
  }

  /**
   * Genera nuovi codici di recupero; quelli vecchi smettono di valere.
   *
   * @param code Codice a sei cifre dell'app di autenticazione.
   * @returns I codici nuovi, da mostrare una volta sola.
   */
  async regenerateRecoveryCodes(code: string): Promise<string[]> {
    const body = await this.client.request<unknown>(
      'POST',
      `${AUTH}/mfa/recovery-codes`,
      { code },
      CREDENTIAL_ROUTE,
    );
    return recoveryCodesOf(body);
  }

  /**
   * Elenca i telefoni registrati per le notifiche.
   *
   * @returns I telefoni.
   */
  listDevices(): Promise<PushDevice[]> {
    return this.client.request<PushDevice[]>('GET', '/api/v1/notifications/devices');
  }

  /**
   * Toglie un telefono: non riceve più notifiche finché l'app non lo registra di nuovo.
   *
   * @param deviceId Registrazione da togliere.
   * @returns Nulla.
   */
  removeDevice(deviceId: string): Promise<void> {
    return this.client.request<void>('DELETE', `/api/v1/notifications/devices/${encodeURIComponent(deviceId)}`);
  }

  /**
   * Dice quali modi di accesso sono accesi (ADR 0033).
   *
   * @returns Google acceso o no, e chi può entrare con la password.
   */
  async loginMethods(): Promise<LoginMethods> {
    const body = await this.client.request<LoginMethods>(
      'GET',
      `${AUTH}/methods`,
      undefined,
      CREDENTIAL_ROUTE,
    );
    const password = PASSWORD_MODES.includes(body.password) ? body.password : 'all';
    return { google: body.google === true, password };
  }

  /**
   * Dice se il recupero della password via email è acceso (ADR 0030 §6.4).
   *
   * @returns True se l'email di reimpostazione parte davvero.
   */
  async passwordResetEnabled(): Promise<boolean> {
    const body = await this.client.request<{ enabled: boolean }>(
      'GET',
      `${AUTH}/password/forgot`,
      undefined,
      CREDENTIAL_ROUTE,
    );
    return body.enabled;
  }

  /**
   * Chiede l'email di reimpostazione della password. La risposta è sempre la stessa,
   * che il nome esista o no.
   *
   * @param username Nome utente.
   * @returns Nulla.
   * @throws ApiError 429 oltre il limite di richieste.
   */
  async requestPasswordReset(username: string): Promise<void> {
    await this.client.request<unknown>('POST', `${AUTH}/password/forgot`, { username }, CREDENTIAL_ROUTE);
  }

  /**
   * Legge le preferenze delle notifiche per evento.
   *
   * @returns Le preferenze.
   */
  notificationPreferences(): Promise<NotificationPreferences> {
    return this.client.request<NotificationPreferences>('GET', '/api/v1/notifications/preferences');
  }

  /**
   * Salva la scelta per un evento.
   *
   * @param event Evento.
   * @param push Push accesa.
   * @returns Tutte le preferenze aggiornate.
   */
  saveNotificationPreference(event: NotificationEvent, push: boolean): Promise<NotificationPreferences> {
    return this.client.request<NotificationPreferences>('PUT', '/api/v1/notifications/preferences', {
      events: [{ event, push }],
    });
  }

  /**
   * Stato dell'ultima esportazione dei dati.
   *
   * @returns Lo stato.
   */
  dataExport(): Promise<DataExport> {
    return this.client.request<DataExport>('GET', '/api/v1/me/export');
  }

  /**
   * Chiede l'esportazione dei dati: l'archivio si prepara in background.
   *
   * @returns Lo stato della richiesta.
   * @throws ApiError 409 `export_in_progress` con una richiesta in corso.
   */
  requestDataExport(): Promise<DataExport> {
    return this.client.request<DataExport>('POST', '/api/v1/me/export');
  }

  /**
   * Cancella l'account, dopo nome utente e password.
   *
   * @param username Nome utente scritto per intero.
   * @param password Password attuale; assente per chi è entrato con Google, che conferma
   *   con un accesso recente.
   * @returns Nulla.
   * @throws ApiError 400 `confirmation_mismatch`, `invalid_current_password` o
   *   `password_required`, 409 `admin_cannot_delete` o `reauth_required`, 429, 503
   *   `deletion_incomplete`.
   */
  async deleteAccount(username: string, password?: string): Promise<void> {
    const body = password === undefined ? { username } : { username, password };
    await this.client.request<unknown>('POST', '/api/v1/me/delete', body, CREDENTIAL_ROUTE);
  }

  /**
   * Azzera la verifica in due passaggi di un utente; solo per l'amministratore
   * della piattaforma. L'utente la registra di nuovo al prossimo accesso.
   *
   * @param username Nome utente.
   * @returns Nulla.
   * @throws ApiError 403 se il chiamante non è amministratore, 404 se l'utente non l'ha registrata.
   */
  async resetMfa(username: string): Promise<void> {
    await this.client.request<unknown>('POST', `${AUTH}/mfa/reset`, { username });
  }
}
